/**
 * LessonPreviewIllustration — Blobby + rainbow scene
 *
 * Replaces the static rainbow SVG in the Landing credibility card.
 * Mascot-coherent illustration: Blobby (science mascot) looks up at a
 * rainbow arc while holding a single raindrop with light bending through it.
 *
 * Visual vocabulary borrowed directly from src/components/mascots/Mascot.tsx
 * — same body silhouette (viewBox 0 0 193 162), same SplitEyes shapes,
 * same teal stroke + mint fill, same mouth curve idiom.
 *
 * Continuous micro-motion:
 *  - Blobby blinks every 4–6s (randomised)
 *  - Rainbow arcs shimmer between opacity 0.85 ↔ 1.0 over 6s
 *  - Raindrop highlight gently drifts (subtle light-bending cue)
 *
 * Respects `prefers-reduced-motion`: all loops disabled, scene renders static.
 *
 * Responsive: SVG scales fluidly from 320px to 720px width.
 */

import { useEffect, useState, useSyncExternalStore } from 'react';
import { motion } from 'framer-motion';

// Brand palette pulled from CLAUDE.md
const SCIENCE_FILL = '#71FFDE';
const SCIENCE_STROKE = '#00D3A5';
const SCIENCE_FACE = '#00D3A5';

// Rainbow palette — softened from textbook ROYGBIV to feel cohesive with sand bg
const RAINBOW_BANDS = [
  '#E53F1F', // engineering red
  '#FD9E0A', // math amber
  '#FDE35C', // math yellow
  '#63C127', // nature green
  '#2970E0', // tech blue
  '#954BAE', // arts purple
];

// ─── Reduced-motion hook ─────────────────────────────────────────────────────

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function subscribeReducedMotion(onChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mql = window.matchMedia(REDUCED_MOTION_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

function getReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReducedMotion, getReducedMotion, () => false);
}

// ─── Blinking hook (matches Mascot.tsx cadence: 2–6s) ────────────────────────

function useBlinking(enabled: boolean): boolean {
  const [blinking, setBlinking] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let next: ReturnType<typeof setTimeout>;
    let close: ReturnType<typeof setTimeout>;
    const schedule = () => {
      next = setTimeout(() => {
        if (cancelled) return;
        setBlinking(true);
        close = setTimeout(() => {
          if (cancelled) return;
          setBlinking(false);
          schedule();
        }, 120);
      }, 4000 + Math.random() * 2000);
    };
    schedule();
    return () => {
      cancelled = true;
      clearTimeout(next);
      clearTimeout(close);
    };
  }, [enabled]);
  return blinking;
}

// ─── Component ───────────────────────────────────────────────────────────────

export interface LessonPreviewIllustrationProps {
  className?: string;
  /** alt-equivalent for screen readers; pass localized string from Landing */
  ariaLabel?: string;
}

export function LessonPreviewIllustration({
  className,
  ariaLabel,
}: LessonPreviewIllustrationProps) {
  const reduced = usePrefersReducedMotion();
  const blinking = useBlinking(!reduced);

  // Shimmer transition — disabled when reduced motion is set
  const shimmerTransition = reduced
    ? { duration: 0 }
    : { duration: 6, repeat: Infinity, ease: 'easeInOut' as const };

  return (
    <svg
      viewBox="0 0 360 260"
      className={className}
      role="img"
      aria-label={ariaLabel ?? 'Blobby the science mascot looks up at a rainbow'}
      data-testid="lesson-preview-illustration"
    >
      {/* ─── Rainbow arcs in the sky ───────────────────────────── */}
      {/* Each band is a stroked arc with shimmer animation */}
      <g aria-hidden="true">
        {RAINBOW_BANDS.map((color, i) => {
          // Arcs widen outward: innermost radius 70, each band +9
          const radius = 70 + i * 9;
          // Arc from left to right across the top
          const d = `M ${180 - radius} 200 A ${radius} ${radius} 0 0 1 ${180 + radius} 200`;
          return (
            <motion.path
              key={color}
              d={d}
              stroke={color}
              strokeWidth="9"
              strokeLinecap="round"
              fill="none"
              initial={{ opacity: 0.92 }}
              animate={reduced ? { opacity: 0.92 } : { opacity: [0.85, 1, 0.85] }}
              transition={{ ...shimmerTransition, delay: i * 0.18 }}
            />
          );
        })}
      </g>

      {/* ─── Soft sun glow in upper-left ─────────────────────────── */}
      <circle cx="58" cy="58" r="22" fill="#FDE35C" opacity="0.85" aria-hidden="true" />
      <circle cx="58" cy="58" r="32" fill="#FDE35C" opacity="0.18" aria-hidden="true" />

      {/* ─── Blobby — pulled from Mascot.tsx, science variant ───── */}
      {/* Positioned bottom-center, scaled down to ~120w, looking up   */}
      <g
        transform="translate(110 110) scale(0.72)"
        aria-hidden="true"
      >
        {/* Outer border (stroke colour shape) */}
        <path
          d="M106.185 161.614C81.7137 163.073 54.3123 160.535 31.4429 151.489C8.73701 142.502 -5.40185 121.533 1.96187 97.2605C3.98264 90.602 7.20018 84.7743 10.9213 78.8942C21.6333 61.9734 17.134 53.2677 25.8711 34.9603C35.7133 14.3244 55.594 1.41308 78.3915 0.098395C95.6956 -0.902332 111.123 5.98502 122.168 19.0926C129.375 27.6478 139.446 32.0628 149.956 35.2023C175.604 42.8746 192.444 65.3615 192.987 91.969C193.556 120.133 177.919 141.384 151.996 151.895C137.34 157.84 122.038 160.673 106.192 161.614H106.185Z"
          fill={SCIENCE_STROKE}
        />
        {/* Body fill */}
        <path
          d="M182.406 72.085C176.311 56.4593 163.218 45.5952 147.189 40.9317C134.967 37.3736 124.281 31.7551 115.877 22.0291C110.161 15.4164 103.014 10.3539 94.2765 8.14972C74.69 3.21149 53.8152 9.62137 40.3042 24.6257C32.6462 33.1286 28.1992 43.4302 26.3746 54.6606C24.6612 65.2173 21.3783 74.7275 15.3552 83.5901C14.335 85.0879 10.2346 94.2319 10.2346 94.2319C2.16459 110.649 7.42252 129.002 23.1244 139.729C30.9197 145.053 39.3559 148.206 48.7143 150.39C76.1549 156.8 104.596 157.323 132.135 151.378C154.88 146.466 177.023 134.084 183.936 110.59C187.677 97.8816 187.232 84.4535 182.406 72.085Z"
          fill={SCIENCE_FILL}
        />
        {/* Face — translated up by 12 to make Blobby look up at the rainbow */}
        <g transform="translate(0 -12)">
          {/* Eyes — filled paths from Mascot.tsx, scaleY 0.05 when blinking */}
          <path
            d="M92.6808 88.6134C101.614 89.359 99.4559 80.3329 104.308 81.0981C106.368 81.4252 108.114 83.5836 107.323 86.0298C105.355 92.093 99.3382 95.455 93.2693 95.4026C87.2005 95.3503 81.3605 92.0146 79.3724 85.9121C78.6792 83.7864 79.9022 81.7718 81.6483 81.2224C87.037 79.5284 84.1007 87.9005 92.6808 88.6134Z"
            fill={SCIENCE_FACE}
            style={{
              transformBox: 'fill-box',
              transformOrigin: 'center',
              transform: blinking ? 'scaleY(0.05)' : 'scaleY(1)',
              transition: 'transform 80ms ease-out',
            }}
          />
          <path
            d="M151.636 88.548C160.511 89.5356 159.392 82.0334 162.374 81.2028C163.65 80.8496 165.049 81.1177 166.089 82.3212C166.991 83.3612 167.286 84.9048 166.612 86.5858C164.33 92.3023 158.614 95.383 152.676 95.3699C146.901 95.3634 141.101 92.322 138.864 86.5923C138.047 84.5058 138.576 82.3474 140.65 81.3728C145.41 79.1228 144.168 87.7173 151.636 88.548Z"
            fill={SCIENCE_FACE}
            style={{
              transformBox: 'fill-box',
              transformOrigin: 'center',
              transform: blinking ? 'scaleY(0.05)' : 'scaleY(1)',
              transition: 'transform 80ms ease-out',
            }}
          />
          {/* Mouth — happy curve (matches mascot idle state) */}
          <path
            d="M102 107 C109 116 134 116 141 106"
            stroke={SCIENCE_FACE}
            strokeWidth="5"
            strokeLinecap="round"
            fill="none"
          />
        </g>
      </g>

      {/* ─── Raindrop Blobby is holding (right side, light bending) ─── */}
      <g
        transform="translate(258 168)"
        aria-hidden="true"
      >
        {/* Outer raindrop teardrop shape — pointed top, rounded bottom */}
        <path
          d="M 0 -34 C 18 -10 18 8 8 18 C -2 26 -16 24 -20 14 C -24 4 -18 -14 0 -34 Z"
          fill="#7DBEF3"
          stroke="#2970E0"
          strokeWidth="2.2"
          opacity="0.92"
        />
        {/* Inner light highlight — gently drifts to suggest refraction */}
        <motion.ellipse
          cx="-6"
          cy="-8"
          rx="4"
          ry="7"
          fill="#FFFFFF"
          opacity="0.75"
          animate={reduced ? {} : { cx: [-6, -4, -6], cy: [-8, -10, -8] }}
          transition={reduced ? { duration: 0 } : { duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* Tiny refracted spectrum exiting bottom of drop — visual echo of the rainbow */}
        {RAINBOW_BANDS.map((color, i) => (
          <motion.line
            key={`refract-${color}`}
            x1="6"
            y1="18"
            x2={18 + i * 1.2}
            y2={28 + i * 2}
            stroke={color}
            strokeWidth="1.6"
            strokeLinecap="round"
            initial={{ opacity: 0.85 }}
            animate={reduced ? { opacity: 0.85 } : { opacity: [0.7, 1, 0.7] }}
            transition={{ ...shimmerTransition, delay: i * 0.18 + 1 }}
          />
        ))}
      </g>
    </svg>
  );
}

export default LessonPreviewIllustration;
